import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Keyboard } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useUrlFilters } from '@/lib/useUrlFilters'
import { useMe } from '@/features/auth/hooks'
import { useAlertsInfinite } from './hooks/useAlertsInfinite'
import { useBulkSelection } from './hooks/useBulkSelection'
import { useQueueKeyboard } from './hooks/useQueueKeyboard'
import { useAlertsWebSocket } from './hooks/useAlertsWebSocket'
import { useSavedFilters } from './hooks/useSavedFilters'
import { AlertsTable } from './components/queue/AlertsTable'
import { FilterBar } from './components/queue/FilterBar'
import { SavedFilterTabs } from './components/queue/SavedFilterTabs'
import { BulkActionsBar } from './components/queue/BulkActionsBar'
import { LiveIndicator } from './components/queue/LiveIndicator'
import { NewAlertsToast } from './components/queue/NewAlertsToast'
import { ShortcutsModal } from './components/queue/ShortcutsModal'
import { ExportCsvButton } from './components/queue/ExportCsvButton'
import { SaveFilterDialog } from './components/queue/SaveFilterDialog'
import { DEFAULT_STATUS, queueUrlCodec } from './queueUrlState'
import { normalizeChannels } from './types'
import type { AlertFilters, SavedFilter, SortKey } from './types'

/**
 * AF-126 — alerts queue page.
 *
 * Composes saved-filter tabs + filter chip bar + virtualized table + bulk
 * actions bar + real-time indicator. Keyboard shortcuts (j/k/Enter/space/x/?)
 * scoped to this page via `useQueueKeyboard`.
 *
 * URL is the single source of truth for the view (`useUrlFilters` +
 * `queueUrlCodec`): status / priority / channel / scenario / queue /
 * assigned_to / q / sla_breach / amount / from / to / sort / saved are all
 * parsed from the address bar on mount AND on every external navigation,
 * and every in-page edit is written back with `replace`. Dashboard tiles
 * (`/alerts?assigned_to=..&status=open`, `/alerts?saved=mine`) therefore
 * open the exact view they promise, and refresh / back / shared links are
 * faithful. There is no local filter state to drift from the URL.
 */
export function AlertsQueuePage() {
  const [urlState, setUrlState] = useUrlFilters(queueUrlCodec)
  const filters = urlState.filters
  const sort = urlState.sort
  const activeSavedId = urlState.saved
  const { data: me } = useMe()
  const savedQuery = useSavedFilters()

  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [userIsAtTop, setUserIsAtTop] = useState(true)
  const [saveFilterOpen, setSaveFilterOpen] = useState(false)
  // Non-blocking notice (saved view missing / legacy channel dropped).
  const [notice, setNotice] = useState<string | null>(null)

  // Saved filters may predate the wire channel vocabulary: translate legacy
  // values and surface any that cannot be mapped instead of silently sending
  // a filter the backend would reject (or dropping it unseen).
  const loadSavedFilters = useCallback((f: AlertFilters): AlertFilters => {
    if (!f.channel || f.channel.length === 0) return f
    const { channels, dropped } = normalizeChannels(f.channel)
    if (dropped.length > 0) {
      setNotice(
        `Ignored unknown channel value${dropped.length > 1 ? 's' : ''} in this saved view: ${dropped.join(', ')}.`,
      )
    }
    return { ...f, channel: channels.length > 0 ? channels : undefined }
  }, [])

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  // Saved-view deep links (`?saved=mine`, `?saved=<id>`): resolve the alias
  // to concrete filters once the data needed is available, and write them
  // into the URL so the chips on screen match what is requested. The ref
  // guards against re-applying (and against fighting an operator who then
  // edits the filters — any FilterBar edit also clears `saved`).
  const resolvedSavedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!activeSavedId || resolvedSavedRef.current === activeSavedId) return
    let resolved: AlertFilters | undefined
    if (activeSavedId === 'mine') {
      if (me) resolved = { status: [...DEFAULT_STATUS], assigned_to: [me.id] }
    } else {
      resolved = savedQuery.data?.find((f) => f.id === activeSavedId)?.filters
      if (!resolved && savedQuery.isSuccess) {
        // List loaded and the id is not in it (deleted / someone else's /
        // stale link): drop the dangling param and say so.
        resolvedSavedRef.current = activeSavedId
        // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot, guarded by resolvedSavedRef
        setNotice('That saved view is no longer available. Showing the default alert queue.')
        setUrlState((prev) => ({ ...prev, saved: null }))
        return
      }
    }
    if (!resolved) return
    resolvedSavedRef.current = activeSavedId
    const next = loadSavedFilters(resolved)
    setUrlState((prev) => ({ ...prev, filters: next }))
  }, [activeSavedId, me, savedQuery.data, savedQuery.isSuccess, setUrlState, loadSavedFilters])

  const setFilters = useCallback(
    (next: AlertFilters) => setUrlState((prev) => ({ ...prev, filters: next, saved: null })),
    [setUrlState],
  )

  const queryArgs = useMemo(() => ({ filters, sort }), [filters, sort])
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAlertsInfinite(queryArgs)
  const alerts = useMemo(() => data?.pages.flatMap((p) => p.alerts) ?? [], [data?.pages])
  const total = data?.pages[0]?.total ?? 0

  const selection = useBulkSelection<string>()

  const ws = useAlertsWebSocket({ userIsAtTop })

  // Track scrollTop to decide when to buffer vs. fade-in new alerts.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => setUserIsAtTop(el.scrollTop <= 100)
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  // Document title pings while tab is hidden and new alerts are buffered.
  useEffect(() => {
    if (ws.bufferedNewCount > 0 && document.visibilityState === 'hidden') {
      document.title = `(${ws.bufferedNewCount}) Antifraud — alerts`
    } else {
      document.title = 'Antifraud — alerts'
    }
    return () => {
      document.title = 'Antifraud'
    }
  }, [ws.bufferedNewCount])

  const handleSort = (field: SortKey['field'], multi: boolean) => {
    const existing = sort.find((s) => s.field === field)
    let next: SortKey[]
    if (!multi) {
      next = existing
        ? [{ field, dir: existing.dir === 'asc' ? 'desc' : 'asc' }]
        : [{ field, dir: 'desc' }]
    } else if (existing) {
      // Multi-sort with shift-click — flip if already in the list…
      next = sort.map((s) =>
        s.field === field ? { ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' } : s,
      )
    } else {
      // …or append.
      next = [...sort, { field, dir: 'desc' }]
    }
    setUrlState((prev) => ({ ...prev, sort: next }))
  }

  const orderedIds = useMemo(() => alerts.map((a) => a.id), [alerts])

  const onToggleSelect = (id: string, range: boolean) => {
    selection.toggle(id, range, orderedIds)
  }

  const onToggleSelectAll = () => {
    if (selection.count === alerts.length) selection.clear()
    else selection.setAll(orderedIds)
  }

  // Keyboard focus is tracked by alert ID, never by row index, and is
  // dropped whenever the result set definition (filters / sort) changes.
  const keyboardResetKey = useMemo(() => JSON.stringify([filters, sort]), [filters, sort])
  const { focusedIndex, setFocusedId } = useQueueKeyboard({
    alertIds: orderedIds,
    resetKey: keyboardResetKey,
    onToggleSelect,
    onToggleSelectAll,
    onClearSelection: selection.clear,
    onShowShortcuts: () => setShortcutsOpen(true),
    onFocusSearch: () => searchInputRef.current?.focus(),
  })

  const handleSelectSavedFilter = (sf: SavedFilter | null) => {
    resolvedSavedRef.current = sf?.id ?? null
    // "All open" (null) restores the default actionable-status view.
    setUrlState((prev) => ({
      ...prev,
      filters: sf ? loadSavedFilters(sf.filters) : { status: [...DEFAULT_STATUS] },
      saved: sf?.id ?? null,
    }))
  }

  const handleClearFilters = () => {
    resolvedSavedRef.current = null
    setUrlState((prev) => ({ ...prev, filters: {}, saved: null }))
  }

  // "Reset to my queue" never looks a built-in up by id/name: the server's
  // built-ins (migration 0082) carry random public_id UUIDs and their "My
  // Queue" filters have no assignee. It applies the same default view as the
  // `?saved=mine` alias (active statuses + assigned to the signed-in user).
  const handleResetToMyQueue = () => {
    if (!me) return
    resolvedSavedRef.current = 'mine'
    setUrlState((prev) => ({
      ...prev,
      filters: { status: [...DEFAULT_STATUS], assigned_to: [me.id] },
      saved: 'mine',
    }))
  }

  // Chip-level "Remove Queue filter" (FilterBar).
  const handleClearQueueFilter = () => {
    setUrlState((prev) => ({ ...prev, filters: { ...prev.filters, queue_id: undefined } }))
  }

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col gap-3">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-xl font-semibold">Alerts</h1>
          <p className="text-xs text-muted-fg">
            {total} total · {alerts.length} loaded
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LiveIndicator
            paused={ws.paused}
            bufferedCount={ws.bufferedNewCount}
            onTogglePause={() => ws.setPaused(!ws.paused)}
          />
          <ExportCsvButton filters={filters} sort={sort} />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Keyboard shortcuts"
            onClick={() => setShortcutsOpen(true)}
          >
            <Keyboard size={16} />
          </Button>
        </div>
      </header>

      {notice ? (
        <div
          role="status"
          className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted px-3 py-2 text-xs"
        >
          <span>{notice}</span>
          <Button variant="ghost" size="sm" onClick={() => setNotice(null)}>
            Dismiss
          </Button>
        </div>
      ) : null}

      <SavedFilterTabs
        activeId={activeSavedId}
        onSelect={handleSelectSavedFilter}
        onCreate={() => setSaveFilterOpen(true)}
      />

      <FilterBar
        filters={filters}
        onChange={setFilters}
        onClearAll={handleClearFilters}
        onClearQueue={handleClearQueueFilter}
      />

      <div className="relative flex-1 min-h-0">
        <NewAlertsToast
          count={ws.bufferedNewCount}
          onClick={() => {
            ws.acknowledgeBuffered()
            scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
          }}
        />
        <AlertsTable
          alerts={alerts}
          loading={isLoading}
          error={error}
          filters={filters}
          sort={sort}
          selectedIds={selection.selected}
          focusedIndex={focusedIndex}
          onFocusRow={setFocusedId}
          recentlyChangedIds={ws.recentlyChangedIds}
          onSort={handleSort}
          onToggleSelect={onToggleSelect}
          onToggleSelectAll={onToggleSelectAll}
          onClearFilters={handleClearFilters}
          onResetToMyQueue={handleResetToMyQueue}
          onRetry={() => void refetch()}
          onLoadMore={() => void fetchNextPage()}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          scrollContainerRef={scrollRef}
        />
      </div>

      <BulkActionsBar
        selectedIds={Array.from(selection.selected)}
        alerts={alerts}
        onClear={selection.clear}
      />

      <ShortcutsModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <SaveFilterDialog
        open={saveFilterOpen}
        onOpenChange={setSaveFilterOpen}
        filters={filters}
        onSaved={(saved) => {
          resolvedSavedRef.current = saved.id
          setUrlState((prev) => ({ ...prev, saved: saved.id }))
        }}
      />
    </div>
  )
}
